import { useMemo } from "react";
import { Stack } from "@mui/material";
import PropTypes from "prop-types";

import ChartContainer from "../../components/shared/ChartContainer";
import {
  InsightsChartSkeleton,
  InsightsEmptyState,
  InsightsErrorState,
} from "../../components/Insights/InsightsAsyncState";
import DesignAccordion from "./design/Accordion";
import { chartJsCategoryAxisTicksWithWordWrap } from "./chartJsCategoryAxisTickWordWrap";
import { cadCurrencyFormat } from "./constants";
import {
  buildProvinceAvgUnitPriceChart,
  buildProvinceSnapshotBarBackgroundColors,
  buildRetailerSnapshotBars,
} from "./productSnapshotChartUtils";
import { PRICING_REPORT_SCREEN_CHART_PLOT_HEIGHT_PX } from "./pricingReportPrintDimensions";
import { resolveInsightsBarChartColor } from "./pricingChartsUtils";
import {
  snapshotProvinceChartDescription,
  snapshotProvinceChartTitle,
  snapshotRetailerChartDescription,
  snapshotRetailerChartTitle,
} from "./pricingReportChartCopy";
import { formatRetailerSnapshotChartLabel } from "./retailerSnapshotChartDisplayNames";

function ProductSnapshotChartSlot({ isLoading, error, isEmpty, emptyMessage, children }) {
  if (isLoading) {
    return <InsightsChartSkeleton />;
  }
  if (error) {
    return (
      <InsightsErrorState
        message={error}
        fallbackMessage="Something went wrong while loading chart data."
      />
    );
  }
  if (isEmpty) {
    return <InsightsEmptyState message={emptyMessage} />;
  }
  return children;
}

/**
 * Product Snapshot accordion (Insights → Pricing Reports).
 */
export default function ProductSnapshotPricingReportsBlock({
  chartDateFrom,
  chartDateTo,
  productName,
  selectedProvince = null,
  selectedRetailers = [],
  retailerSnapshotRows,
  isRetailerSnapshotLoading,
  retailerSnapshotError,
  provinceSnapshotRows,
  isProvinceSnapshotLoading,
  provinceSnapshotError,
  accordionExpanded,
  onAccordionExpandedChange,
  onAccordionEntered,
  chartRefSnapshotRetailer,
  chartRefSnapshotProvince,
}) {
  const sortedRetailerBars = useMemo(
    () => buildRetailerSnapshotBars(retailerSnapshotRows),
    [retailerSnapshotRows]
  );

  const retailerChart = useMemo(() => {
    const labels = sortedRetailerBars.map((bar) => formatRetailerSnapshotChartLabel(bar.retailer));
    const data = sortedRetailerBars.map((bar) => Number(bar.avgUnitPrice.toFixed(2)));
    return { labels, data };
  }, [sortedRetailerBars]);

  const provinceChart = useMemo(
    () => buildProvinceAvgUnitPriceChart(provinceSnapshotRows),
    [provinceSnapshotRows]
  );

  const provinceBarBackgroundColors = useMemo(
    () =>
      buildProvinceSnapshotBarBackgroundColors(
        provinceChart.provinceCodes,
        selectedProvince?.value
      ),
    [provinceChart.provinceCodes, selectedProvince?.value]
  );

  const retailerChartOptions = useMemo(
    () => ({
      layout: {
        padding: {
          bottom: 8,
        },
      },
      scales: {
        x: {
          ticks: chartJsCategoryAxisTicksWithWordWrap(),
        },
        y: {
          title: {
            display: true,
            text: "Avg unit price (CAD)",
          },
        },
      },
      plugins: {
        tooltip: {
          displayColors: false,
          callbacks: {
            title: (tooltipItems) => {
              const first = tooltipItems?.[0];
              if (first == null) return "";
              const bar = sortedRetailerBars[first.dataIndex];
              if (!bar) return "";
              return `${bar.retailer} - ${bar.priceHistoryRowCount} Data Points`;
            },
            label: (ctx) => {
              const bar = sortedRetailerBars[ctx.dataIndex];
              if (!bar) return "";
              return cadCurrencyFormat.format(bar.avgUnitPrice);
            },
          },
        },
      },
    }),
    [sortedRetailerBars]
  );

  const provinceChartOptions = useMemo(
    () => ({
      layout: {
        padding: {
          bottom: 8,
        },
      },
      scales: {
        x: {
          ticks: chartJsCategoryAxisTicksWithWordWrap(),
        },
        y: {
          title: {
            display: true,
            text: "Avg unit price (CAD)",
          },
        },
      },
    }),
    []
  );

  const chartDateContext = { dateFrom: chartDateFrom, dateTo: chartDateTo };
  const chartATitle = snapshotRetailerChartTitle(productName);
  const chartADescription = snapshotRetailerChartDescription(productName, chartDateContext);
  const chartBTitle = snapshotProvinceChartTitle(productName);
  const chartBDescription = snapshotProvinceChartDescription(productName, chartDateContext);
  const chartAEmptyMessage = `No price history by retailer for ${productName} in the selected date range.`;
  const chartBEmptyMessage = `No price history by province for ${productName} in the selected date range.`;

  const retailerChartHasData = sortedRetailerBars.length > 0;
  const retailerSnapshotIsEmpty =
    !isRetailerSnapshotLoading && retailerSnapshotRows !== undefined && !retailerChartHasData;
  const provinceSnapshotIsEmpty =
    !isProvinceSnapshotLoading &&
    provinceSnapshotRows !== undefined &&
    !provinceChart.provinceChartHasAnyData;

  return (
    <DesignAccordion
      title="Price comparison"
      subtitle="- Compare product pricing across retailers and provinces."
      expanded={accordionExpanded}
      onChange={onAccordionExpandedChange}
      onEntered={onAccordionEntered}
    >
      <Stack spacing={3}>
          {!selectedRetailers.length ? (
            <ProductSnapshotChartSlot
              isLoading={isRetailerSnapshotLoading}
              error={retailerSnapshotError}
              isEmpty={retailerSnapshotIsEmpty}
              emptyMessage={chartAEmptyMessage}
            >
              <ChartContainer
                title={chartATitle}
                description={chartADescription}
                pricingReportHeadings
                labels={retailerChart.labels}
                chartOptions={retailerChartOptions}
                chartPlotHeight={PRICING_REPORT_SCREEN_CHART_PLOT_HEIGHT_PX}
                chartRef={retailerChartHasData ? chartRefSnapshotRetailer : undefined}
                datasets={[
                  {
                    label: "Avg unit price",
                    data: retailerChart.data,
                    backgroundColor: resolveInsightsBarChartColor(),
                  },
                ]}
              />
            </ProductSnapshotChartSlot>
          ) : null}

          <ProductSnapshotChartSlot
            isLoading={isProvinceSnapshotLoading}
            error={provinceSnapshotError}
            isEmpty={provinceSnapshotIsEmpty}
            emptyMessage={chartBEmptyMessage}
          >
            <ChartContainer
              title={chartBTitle}
              description={chartBDescription}
              pricingReportHeadings
              labels={provinceChart.labels}
              chartOptions={provinceChartOptions}
              chartPlotHeight={PRICING_REPORT_SCREEN_CHART_PLOT_HEIGHT_PX}
              chartRef={
                provinceChart.provinceChartHasAnyData ? chartRefSnapshotProvince : undefined
              }
              datasets={[
                {
                  label: "Avg unit price",
                  data: provinceChart.data,
                  backgroundColor: provinceBarBackgroundColors,
                },
              ]}
            />
          </ProductSnapshotChartSlot>
        </Stack>
    </DesignAccordion>
  );
}

ProductSnapshotPricingReportsBlock.propTypes = {
  chartDateFrom: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
    PropTypes.object,
  ]),
  chartDateTo: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
    PropTypes.object,
  ]),
  productName: PropTypes.string,
  selectedProvince: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  }),
  selectedRetailers: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
    })
  ),
  retailerSnapshotRows: PropTypes.array,
  isRetailerSnapshotLoading: PropTypes.bool,
  retailerSnapshotError: PropTypes.string,
  provinceSnapshotRows: PropTypes.array,
  isProvinceSnapshotLoading: PropTypes.bool,
  provinceSnapshotError: PropTypes.string,
  accordionExpanded: PropTypes.bool,
  onAccordionExpandedChange: PropTypes.func,
  onAccordionEntered: PropTypes.func,
  chartRefSnapshotRetailer: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  chartRefSnapshotProvince: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
};
